import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { DraftActionButtonsProps } from "./types";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Edit,
  Send,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  MoreHorizontal,
  AlertTriangle,
} from "lucide-react";

export const DraftActionButtons: React.FC<DraftActionButtonsProps> = ({
  draft,
  showEdit = true,
  showPublish = true,
  showDelete = true,
  showReview = true,
  showPreview = true,
  onEdit,
  onPublish,
  onDelete,
  onReview,
  onPreview,
  size = "sm",
  variant = "inline",
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);

  const handleEdit = () => {
    onEdit?.();
  };

  const handlePreview = () => {
    onPreview?.();
  };

  const handlePublish = () => {
    if (draft.status === "approved" || draft.status === "draft") {
      setShowPublishDialog(true);
    }
  };

  const handleConfirmPublish = () => {
    onPublish?.();
    setShowPublishDialog(false);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    onDelete?.();
    setShowDeleteDialog(false);
  };

  const handleReview = (action: "approve" | "reject") => {
    onReview?.(action);
  };

  // Define button configurations
  const buttons = [
    {
      key: "preview",
      label: "Preview",
      icon: <Eye className="w-4 h-4" />,
      onClick: handlePreview,
      show: showPreview && !!onPreview,
      variant: "outline" as const,
      disabled: false,
    },
    {
      key: "edit",
      label: "Edit",
      icon: <Edit className="w-4 h-4" />,
      onClick: handleEdit,
      show: showEdit && !!onEdit,
      variant: "outline" as const,
      disabled: false,
    },
    {
      key: "approve",
      label: "Approve",
      icon: <CheckCircle className="w-4 h-4" />,
      onClick: () => handleReview("approve"),
      show: showReview && draft.status === "pending_review" && !!onReview,
      variant: "default" as const,
      disabled: false,
    },
    {
      key: "reject",
      label: "Reject",
      icon: <XCircle className="w-4 h-4" />,
      onClick: () => handleReview("reject"),
      show: showReview && draft.status === "pending_review" && !!onReview,
      variant: "outline" as const,
      disabled: false,
    },
    {
      key: "publish",
      label: draft.status === "approved" ? "Publish" : "Publish Draft",
      icon: <Send className="w-4 h-4" />,
      onClick: handlePublish,
      show:
        showPublish &&
        (draft.status === "approved" || draft.status === "draft") &&
        !!onPublish,
      variant: "default" as const,
      disabled: false,
    },
    {
      key: "delete",
      label: "Delete",
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDelete,
      show: showDelete && !!onDelete,
      variant: "destructive" as const,
      disabled: false,
    },
  ];

  const visibleButtons = buttons.filter((button) => button.show);

  if (visibleButtons.length === 0) {
    return null;
  }

  const renderInlineButtons = () => (
    <div className="flex items-center space-x-1">
      {visibleButtons.slice(0, 3).map((button) => (
        <Button
          key={button.key}
          size={size}
          variant={button.variant}
          onClick={button.onClick}
          disabled={button.disabled}
          className={cn(
            "flex-shrink-0",
            size === "sm" && "h-7 px-2",
            button.key === "delete" &&
              "text-destructive hover:text-destructive-foreground",
          )}
          aria-label={button.label}
          title={button.label}
        >
          {button.icon}
          <span className="sr-only">{button.label}</span>
        </Button>
      ))}

      {visibleButtons.length > 3 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size={size}
              variant="outline"
              className={cn("flex-shrink-0", size === "sm" && "h-7 px-2")}
              aria-label="More actions"
            >
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {visibleButtons.slice(3).map((button) => (
              <DropdownMenuItem
                key={button.key}
                onClick={button.onClick}
                disabled={button.disabled}
                className={cn(
                  button.key === "delete" &&
                    "text-destructive focus:text-destructive",
                )}
              >
                {button.icon}
                <span className="ml-2">{button.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  const renderDropdownMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size={size}
          variant="outline"
          className={cn("flex-shrink-0", size === "sm" && "h-7 px-2")}
          aria-label="Draft actions"
        >
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {visibleButtons.map((button, index) => (
          <React.Fragment key={button.key}>
            <DropdownMenuItem
              onClick={button.onClick}
              disabled={button.disabled}
              className={cn(
                button.key === "delete" &&
                  "text-destructive focus:text-destructive",
              )}
            >
              {button.icon}
              <span className="ml-2">{button.label}</span>
            </DropdownMenuItem>
            {index === visibleButtons.length - 2 && button.key !== "delete" && (
              <DropdownMenuSeparator />
            )}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      {/* Action Buttons */}
      {variant === "inline" ? renderInlineButtons() : renderDropdownMenu()}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <span>Delete Draft</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the draft "{draft.name}"?
              <span className="block mt-2 text-sm">
                This action cannot be undone. All work on this draft will be
                permanently lost.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Publish Confirmation Dialog */}
      <AlertDialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <Send className="w-5 h-5 text-primary" />
              <span>Publish Draft</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to publish the draft "{draft.name}"?
              <span className="block mt-2 text-sm">
                This will create a new version and make the changes available to
                all users.
                {draft.status === "draft" &&
                  " The draft has not been approved yet."}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmPublish}>
              Publish Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DraftActionButtons;
